/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState, type FC, type JSX } from 'react';
import { useMeasure } from 'react-use';
import { useSpring } from 'react-spring';
import {
  AnimatedContainer,
  ClearButton,
  ContainerItem,
  ContainerLabel,
  ScrollContainer,
  ToggleButton,
  Label,
} from './styled';
// eslint-disable-next-line import/no-cycle
import { ControlType, type ControlProps, type Target } from '../../index';
import { hasValues } from './utils';
import ArrowIcon from './ArrowIcon';
import Arrow from './Arrow';
import useWindowDimensions from '../../../../hooks/useWindowHandler';
import { sumClasses } from '../../../../utils/misc';
import { ShowMoreButton } from '../common/styled';

interface ContainerControlProps extends ControlProps {
  name: string;
  className?: string;
  top?: {
    text: string | JSX.Element;
  };
  controlsClassName?: string;
  contentBefore: any;
  subControls: ControlProps[];
  returnItem: (item: ControlProps, containerRef?: any, index?: number) => React.ReactElement;
  contentAfter: any;
  noCollapse?: boolean;
  onUpdate: (e: Target) => Promise<void>;
  clearAll?: string;
  showMoreLabel?: string;
  hasArrows?: boolean;
  limit?: number;
  hasTwoColumns?: boolean;
}

const countVisibleLabelsInsideLimit = (limit: number, subControls: ControlProps[], hasTwoColumns: boolean) => {
  const result = subControls.reduce(
    (acc, control) => {
      if (acc.checkedOptions < limit && control.label) {
        acc.count += 1;

        control?.options?.forEach((_option, index) => {
          if (acc.checkedOptions < limit) {
            // when 2 columns layout count only 1 every 2 items
            if (hasTwoColumns && index % 2 === 0) {
              acc.checkedOptions += 1;
            } else if (!hasTwoColumns) {
              acc.checkedOptions += 1;
            }
          }
        });
      }

      return acc;
    },
    { count: 0, checkedOptions: 0 }
  );

  return result.count;
};

const getLimitedHeight = (limit: number, subControls: ControlProps[], hasTwoColumns: boolean) => {
  // const padding = 32;

  // options
  const optionHeight = 48;
  const optionsCount = limit + 1; // adding one to the limit because the show more button hides the last option
  const optionsHeight = optionsCount * optionHeight;

  // labels
  const labelHeight = 36;
  const hasLabels =
    subControls.filter((control) => control.control === ControlType.LISTBOX && !!control.label).length > 0;
  const labelsCount = countVisibleLabelsInsideLimit(limit, subControls, hasTwoColumns);
  const labelsHeight = labelHeight * labelsCount;

  if (hasLabels) return optionsHeight + labelsHeight;
  return optionsHeight;
};

const hasActiveSubControl = (subControls: ControlProps[]) =>
  subControls.some(
    (control) => control.control === ControlType.LISTBOX && control?.value && control?.value?.length > 0
  );

const ContainerControl: FC<ContainerControlProps> = ({
  name,
  className,
  top,
  controlsClassName,
  contentBefore,
  subControls,
  returnItem,
  contentAfter,
  noCollapse,
  onUpdate,
  clearAll,
  showMoreLabel,
  hasArrows,
  limit,
  hasTwoColumns,
}) => {
  const hasLimit = limit !== undefined;
  const innerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [animatedContainerRef, { top: containerTop, bottom: containerBottom }] = useMeasure();
  const [viewShowMore, setViewShowMore] = useState<boolean>(
    () => hasLimit && !!showMoreLabel && !hasActiveSubControl(subControls)
  );
  const limitedHeight = useMemo(
    () => (viewShowMore && hasLimit ? getLimitedHeight(limit, subControls, !!hasTwoColumns) : null),
    [viewShowMore, limit]
  );
  const containerHeight = containerTop + containerBottom;
  const [showClearAll, setShowClearAll] = useState<boolean>(clearAll ? hasValues(subControls) : false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [showArrowLeft, setShowArrowLeft] = useState(false);
  const [showArrowRight, setShowArrowRight] = useState(false);
  const { width } = useWindowDimensions();

  const getContainerHeight = () => {
    if (viewShowMore) return isExpanded ? limitedHeight || containerHeight : 0;
    return isExpanded ? containerHeight : 0;
  };

  const style = useSpring({
    from: { height: getContainerHeight() },
    to: { height: isExpanded ? getContainerHeight() : 0 },
  });

  const handleClearAll = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    onUpdate({ target: { name, clear: true } });
  };

  const handleExpand = () => {
    if (!noCollapse) setIsExpanded(!isExpanded);
  };

  const handleArrowClick = (val: string) => {
    const container = scrollContainerRef?.current;
    if (container) {
      if (val === 'left') {
        container.scroll({
          left: container.scrollLeft - 100,
          behavior: 'smooth',
        });
      } else {
        container.scroll({
          left: container.scrollLeft + 100,
          behavior: 'smooth',
        });
      }
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollLeft((e.target as HTMLDivElement).scrollLeft);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      setShowArrowLeft(container.scrollLeft > 0);
      setShowArrowRight(container.scrollLeft < container.scrollWidth - container.clientWidth);
    }
  }, [scrollLeft]);

  useEffect(() => {
    if (clearAll) {
      const val = hasValues(subControls);

      if (val && !showClearAll) {
        setShowClearAll(true);
      } else if (!val && showClearAll) {
        setShowClearAll(false);
      }
    }
  }, [subControls]);

  useEffect(() => {
    if (innerRef.current && hasArrows) {
      const container = innerRef.current;
      const element = container.querySelectorAll('.container-controls')[0];

      if (element.scrollWidth > element.clientWidth) {
        if (!showArrowRight) setShowArrowRight(true);
      } else {
        if (showArrowRight) setShowArrowRight(false);
        if (showArrowLeft) setShowArrowLeft(false);
      }
    }
  }, [width]);

  const handleShowMore = () => {
    setViewShowMore(false);
  };

  return (
    <ContainerItem
      id={name}
      ref={innerRef}
      className={sumClasses([className || '', 'container-item'])}
      data-testid="container-controls"
    >
      {top && (
        <ContainerLabel className="container-label">
          <Label
            type="button"
            className={sumClasses(['label-container-control', !noCollapse ? 'container-collapsible' : ''])}
            onClick={handleExpand}
            aria-expanded={isExpanded}
            disabled={noCollapse}
          >
            {top.text}
            {!noCollapse && (
              <ToggleButton isExpanded={isExpanded} className="toggle-button-container-control">
                <ArrowIcon />
              </ToggleButton>
            )}
          </Label>

          {clearAll && showClearAll && (
            <ClearButton
              onClick={(e) => handleClearAll(e)}
              data-testid="clear-button"
              className="clear-button-container-control"
            >
              {clearAll}
            </ClearButton>
          )}
        </ContainerLabel>
      )}
      <AnimatedContainer
        style={containerHeight || viewShowMore ? style : {}}
        $isExpanded={isExpanded}
        className="accordion"
      >
        {hasArrows ? (
          <>
            {showArrowLeft && <Arrow handleArrowClick={handleArrowClick} />}
            <ScrollContainer
              className={sumClasses(['container-controls', controlsClassName || ''])}
              ref={scrollContainerRef as React.Ref<HTMLDivElement>}
              onScroll={handleScroll}
              data-testid="container-content"
            >
              {contentBefore}
              {subControls.map((subItem: ControlProps, i: number) => returnItem(subItem, innerRef, i))}
              {contentAfter}
            </ScrollContainer>
            {showArrowRight && <Arrow isRightArrow={true} handleArrowClick={handleArrowClick} />}
          </>
        ) : (
          <div
            className={sumClasses(['container-controls', controlsClassName || ''])}
            ref={animatedContainerRef as React.Ref<HTMLDivElement>}
          >
            {contentBefore}
            {subControls.map((subItem: ControlProps, i: number) => returnItem(subItem, innerRef, i))}
            {contentAfter}
          </div>
        )}
        {viewShowMore && <ShowMoreButton onClick={handleShowMore}>{showMoreLabel}</ShowMoreButton>}
      </AnimatedContainer>
    </ContainerItem>
  );
};

export default ContainerControl;
